import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { MDBDataTable } from 'mdbreact';
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';
import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Form,
  FormGroup,
  Label,
} from 'reactstrap';

import { activateAuthLayout } from '../../../../store/actions';
import SweetAlert from 'react-bootstrap-sweetalert';

const GET_TEAMS = gql`
  query {
    Teams {
      TeamId
      Name
      Phone
      Email
    }
  }
`;

const CREATE_TEAM = gql`
  mutation createTeam($TeamInput: TeamInput) {
    createTeam(input: $TeamInput) {
      TeamId
      Name
      Phone
      Email
    }
  }
`;

const UPDATE_TEAM = gql`
  mutation updateTeam($TeamInput: TeamInput) {
    updateTeam(input: $TeamInput) {
      TeamId
      Name
      Phone
      Email
    }
  }
`;

const DELETE_TEAM = gql`
  mutation deleteTeam($TeamId: ID!) {
    deleteTeam(TeamId: $TeamId) {
      TeamId
      Name
    }
  }
`;

const ManageTeams = ({ activateAuthLayout }) => {
  const { data, loading, refetch } = useQuery(GET_TEAMS);

  const [tableData, setTableData] = useState({
    columns: [
      {
        label: ['Team Name', <i key="team_name" className="fa fa-sort ml-2"></i>],
        field: 'team_name',
        sort: 'asc',
      },
      {
        label: ['Phone Number', <i key="phone" className="fa fa-sort ml-2"></i>],
        field: 'phone',
        sort: 'asc',
      },
      {
        label: ['Email Address', <i key="email" className="fa fa-sort ml-2"></i>],
        field: 'email',
        sort: 'asc',
      },
      {
        label: 'Action',
        field: 'actions',
        sort: 'disabled',
      },
    ],
  });

  const [formModal, setFormModal] = useState(false);
  const [formInput, setFormInput] = useState(null);
  const [formType, setFormType] = useState('create');

  const [createTeam] = useMutation(CREATE_TEAM);
  const [updateTeam] = useMutation(UPDATE_TEAM);
  const [deleteTeam] = useMutation(DELETE_TEAM);

  const [mutateResult, setMutateResult] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const showFormModal = useCallback((team = null) => {
    setFormModal(prev => !prev);
    setFormInput(team);
    setFormType(team ? 'edit' : 'create');
  }, []);

  const deleteSelected = useCallback((team) => {
    deleteTeam({ variables: { TeamId: team.TeamId } }).then(() => {
      refetch();
      setMutateResult({ title: 'Success', message: 'Team deleted successfully!', type: 'success', showConfirm: false });
    }).catch((err) => {
      setMutateResult({ title: 'Error', message: err.graphQLErrors?.[0]?.message || 'Failed to delete team.', type: 'danger', showConfirm: true });
    });
  }, [deleteTeam, refetch]);

  useEffect(() => {
    activateAuthLayout();
    refetch();
  }, [activateAuthLayout, refetch]);

  useEffect(() => {
    if (!loading && data) {
      const rows = data.Teams.map((team) => ({
        team_id: team.TeamId,
        team_name: team.Name,
        phone: team.Phone || '—',
        email: team.Email || '—',
        actions: (
          <div>
            <Button color="info" size="sm" className="mr-2" onClick={() => showFormModal(team)}>
              Edit
            </Button>
            <Button color="danger" size="sm" onClick={() => setDeleteTarget(team)}>
              Delete
            </Button>
          </div>
        ),
      }));
      setTableData(prev => ({ ...prev, rows }));
    }
  }, [data, loading, showFormModal]);

  const updateForm = (field, value) => {
    setFormInput(prev => ({ ...prev, [field]: value }));
  };

  const save = (e) => {
    e.preventDefault();
    if (formType === 'edit') {
      const input = {
        TeamId: formInput?.TeamId,
        Name: formInput?.Name || '',
        Phone: formInput?.Phone || null,
        Email: formInput?.Email || null,
      };
      updateTeam({ variables: { TeamInput: input } }).then(() => {
        refetch();
        setMutateResult({ title: 'Success', message: 'Team updated successfully.', type: 'success', showConfirm: false });
        setFormInput(null);
        setFormModal(false);
      });
    } else {
      const input = {
        Name: formInput?.Name || '',
        Phone: formInput?.Phone || null,
        Email: formInput?.Email || null,
      };
      createTeam({ variables: { TeamInput: input } }).then(() => {
        refetch();
        setMutateResult({ title: 'Success', message: 'Team created successfully.', type: 'success', showConfirm: false });
        setFormInput(null);
        setFormModal(false);
      });
    }
  };

  return (
    <React.Fragment>
      {deleteTarget && (
        <SweetAlert
          warning
          title="Confirm"
          onConfirm={() => { deleteSelected(deleteTarget); setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)}
          showCancel
          confirmBtnText="Delete"
          cancelBtnText="Cancel"
          confirmBtnBsStyle="danger"
          cancelBtnBsStyle="secondary"
        >
          Delete team "{deleteTarget.Name}"?
        </SweetAlert>
      )}

      <div className="content">
        <div className="container-fluid">
          <div className="page-title-box">
            <Row className="align-items-center">
              <Col sm="6">
                <h4 className="page-title">Teams</h4>
              </Col>
              <Col sm="6" className="text-right">
                <Button color="success" onClick={() => showFormModal()}>Create New Team</Button>
              </Col>
            </Row>
          </div>
          <div className="rounded bg-white p-4">
            {!loading && tableData.rows && (
              <MDBDataTable
                sortable
                striped
                borderless
                theadColor="table-dark"
                hover
                responsive
                noBottomColumns
                noRecordsFoundLabel="No Teams Found"
                data={tableData}
              />
            )}
          </div>
        </div>

        <Modal isOpen={formModal} toggle={() => showFormModal()}>
          <ModalHeader toggle={() => showFormModal()}>
            {formType === 'edit' ? 'Edit Team' : 'Create New Team'}
          </ModalHeader>
          <ModalBody>
            <Form onSubmit={save}>
              <FormGroup>
                <Label>Team Name <small className="text-danger">*</small></Label>
                <Input
                  type="text"
                  placeholder="Team Name"
                  defaultValue={formInput?.Name || ''}
                  onChange={(e) => updateForm('Name', e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <Label>Phone Number</Label>
                <Input
                  type="tel"
                  placeholder="Phone"
                  defaultValue={formInput?.Phone || ''}
                  onChange={(e) => updateForm('Phone', e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  placeholder="Email"
                  defaultValue={formInput?.Email || ''}
                  onChange={(e) => updateForm('Email', e.target.value)}
                />
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => showFormModal()}>Cancel</Button>
            <Button
              color="primary"
              onClick={save}
              disabled={!formInput?.Name}
            >
              Save
            </Button>
          </ModalFooter>
        </Modal>

        {mutateResult && (
          <SweetAlert
            type={mutateResult.type}
            title={mutateResult.title}
            onConfirm={() => setMutateResult(null)}
            showConfirm={mutateResult.showConfirm}
            timeout={mutateResult.showConfirm ? 0 : 2500}
          >
            {mutateResult.message}
          </SweetAlert>
        )}
      </div>
    </React.Fragment>
  );
};

export default connect(null, { activateAuthLayout })(ManageTeams);
